import styled from '@emotion/styled';
import React, { PropsWithChildren } from 'react';
import {
  borderRadius,
  borderRadiusScaled,
  ComponentColors,
  ComponentSize,
  ComponentWithColorMode,
  ComponentWithSizeMode,
  getComponentColors,
  lineHeightMap,
  spacing,
  spacingScaled,
  TextElement,
} from '../../foundations';
import { Truncate } from '../../utils/Truncate/Truncate';

export interface TagProps extends ComponentWithSizeMode, ComponentWithColorMode {
  iconStart?: React.JSX.Element;
  iconEnd?: React.JSX.Element;
  truncate?: boolean;
  showStroke?: boolean;
  className?: string;
}

export const Tag: React.FC<PropsWithChildren<TagProps>> = ({
  size = 'small',
  colorMode = 'neutral',
  iconStart,
  iconEnd,
  truncate,
  showStroke,
  className,
  children,
}) => {
  const colors = getComponentColors(colorMode, { noGradient: true });
  return (
    <StyledTagWrapper colors={colors} size={size} showStroke={showStroke} className={className}>
      {iconStart}
      <StyledTextElement textStyle="label" size={size}>
        {truncate ? <Truncate wordBreak="break-all">{children}</Truncate> : children}
      </StyledTextElement>
      {iconEnd}
    </StyledTagWrapper>
  );
};

const StyledTagWrapper = styled.span<{ colors: ComponentColors; size: ComponentSize; showStroke?: boolean }>`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  max-width: fit-content;
  border-radius: ${({ size }) => `${borderRadiusScaled(borderRadius.rounded2, size)}`};
  background-color: ${({ colors }) => colors.fillSubjacent};
  padding: ${({ showStroke, size }) => (!showStroke ? spacingScaled(spacing.size1, size) : undefined)};
  border: ${({ showStroke, colors, size }) =>
    showStroke ? `${spacingScaled(spacing.size1, size)} solid ${colors.strokeSubjacent}` : undefined};
  color: ${({ colors }) => colors.onSubjacent};

  & svg {
    width: ${({ size }) => lineHeightMap.label[size]};
    height: ${({ size }) => lineHeightMap.label[size]};
  }
`;

const StyledTextElement = styled(TextElement)`
  padding: ${({ size }) => `0 ${spacingScaled(spacing.size2, size)}`};
`;
